import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiCall, API_BASE } from '../../api';
import { logger, getErrorMessage } from '../../utils/logger';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

// Must match backend BankAccount.account_type choices exactly:
// [('savings', 'Savings'), ('current', 'Current')]
const ACCOUNT_TYPE_OPTIONS = [
  { value: 'savings', label: 'Savings' },
  { value: 'current', label: 'Current' },
];

const BankDetails = ({ ngo, onBack, onHome, onEditProfile }) => {
  const { user, getAccessTokenSilently, logout } = useAuth0();
  const { t } = useTranslation('bankDetails');
  const { t: tc } = useTranslation('common');
  const [form, setForm] = useState({
    holderName: '', accountNumber: '', branchName: '',
    accountType: '', ifsc: '', balance: '',
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const textFields = [
    { key: 'holderName', label: t('accountHolderName') },
    { key: 'accountNumber', label: t('accountNumber') },
    { key: 'branchName', label: t('branchName') },
    { key: 'ifsc', label: t('ifscCode') },
    { key: 'balance', label: t('initialBalance') },
  ];

  const allFieldKeys = [...textFields.map(f => f.key), 'accountType'];

  const validate = () => {
    const newErrors = {};
    allFieldKeys.forEach(key => {
      if (!form[key]) newErrors[key] = tc('required');
    });
    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    setSubmitError('');
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        account_holder_name: form.holderName,
        account_number: form.accountNumber,
        account_type: form.accountType, // already lowercase from <select> value
        branch_name: form.branchName,
        ifsc_code: form.ifsc,
        balance: parseFloat(form.balance) || 0,
      };

      await apiCall(`/api/organizations/${ngo.id}/bank-accounts/`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }, getAccessTokenSilently);

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setForm({ holderName: '', accountNumber: '', branchName: '', accountType: '', ifsc: '', balance: '' });
        setErrors({});
      }, 2000);
    } catch (err) {
      logger.error('API', `POST /api/organizations/${ngo.id}/bank-accounts/ failed`, {
        status: err?.status,
        detail: err,
      });
      setSubmitError(getErrorMessage(err, t('failedToAdd')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar — DESIGN.md §2 (Pattern A: py-4 / gap-3 / flex-1 spacer) */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        {onHome && (
          <button onClick={onHome} aria-label="Home" title="Home" className="text-blue-600 hover:text-blue-700 flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
              <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
            </svg>
          </button>
        )}
        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <span className="text-sm font-medium text-gray-900 flex-1">{t('title')}</span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${API_BASE || ''}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${API_BASE || ''}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {submitError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <p className="text-xs text-red-600">{submitError}</p>
          </div>
        )}

        {textFields.map(field => (
          <div className="mb-4" key={field.key}>
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
              {field.label} <span className="text-red-500">*</span>
            </p>
            <input type="text" value={form[field.key]}
              disabled={submitting}
              onChange={e => setForm({...form, [field.key]: e.target.value})}
              className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"/>
            {errors[field.key] && (
              <p className="text-xs text-red-500 mt-1">{errors[field.key]}</p>
            )}
          </div>
        ))}

        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('accountType')} <span className="text-red-500">*</span>
          </p>
          <select
            value={form.accountType}
            disabled={submitting}
            onChange={e => setForm({ ...form, accountType: e.target.value })}
            className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
          >
            <option value="" disabled>{t('selectAccountType')}</option>
            {ACCOUNT_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {errors.accountType && (
            <p className="text-xs text-red-500 mt-1">{errors.accountType}</p>
          )}
        </div>

        <button onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-lg px-4 py-3 text-sm font-medium transition mt-4">
          {submitting ? t('adding') : t('add')}
        </button>

        {success && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <p className="text-sm text-green-600 font-medium">
              {t('successMessage')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankDetails;